import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Container, Row, Col, ListGroup } from 'react-bootstrap';
import { createEquipment, fetchEquipments, updateEquipment, fetchEquipmentActivities } from './services/api';
import { useNavigate } from 'react-router-dom';
import { Equipment, EquipmentActivity } from './types';

const EquipmentList: React.FC = () => {
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [currentEquipment, setCurrentEquipment] = useState<Equipment | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [expandedEquipment, setExpandedEquipment] = useState<number | null>(null);
  const [activities, setActivities] = useState<Record<number, EquipmentActivity[]>>({});
  const [loadingActivities, setLoadingActivities] = useState<Record<number, boolean>>({});
  const [activitiesError, setActivitiesError] = useState<Record<number, string>>({});
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEquipments();
      setEquipments(data);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch equipment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    setModalMode('add');
    setCurrentEquipment(null);
    setName('');
    setShowModal(true);
  };

  const handleEdit = (equipment: Equipment) => {
    setModalMode('edit');
    setCurrentEquipment(equipment);
    setName(equipment.name);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) return;
    setDeleteId(id);
    try {
      const res = await fetch(`equipment/${id}/`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete equipment');
      setEquipments(equipments.filter(e => e.id !== id));
    } catch (e: any) {
      alert(e.message || 'Failed to delete equipment');
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleEquipment = async (equipment: Equipment) => {
    if (expandedEquipment === equipment.id) {
      // Collapse if already expanded
      setExpandedEquipment(null);
      return;
    }

    // Expand and fetch activities if not already fetched
    setExpandedEquipment(equipment.id);

    if (!activities[equipment.id]) {
      setLoadingActivities(prev => ({ ...prev, [equipment.id]: true }));
      setActivitiesError(prev => ({ ...prev, [equipment.id]: '' }));

      try {
        const equipmentActivities = await fetchEquipmentActivities(equipment.id);
        setActivities(prev => ({ ...prev, [equipment.id]: equipmentActivities }));
      } catch (e: any) {
        setActivitiesError(prev => ({ ...prev, [equipment.id]: e.message || 'Failed to fetch equipment activities' }));
      } finally {
        setLoadingActivities(prev => ({ ...prev, [equipment.id]: false }));
      }
    }
  };

  const handleModalSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modalMode === 'add') {

        const newEquipment = await createEquipment({ name });
        setEquipments([...equipments, newEquipment]);
      } else if (modalMode === 'edit' && currentEquipment) {
        const updated = await updateEquipment(currentEquipment.id, { name });
        setEquipments(equipments.map(e => e.id === updated.id ? updated : e));
      }
      setShowModal(false);
    } catch (e: any) {
      alert(e.message || 'Failed to save equipment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="p-4 p-md-5">
      <Row className="mb-3">
        <Col><h2>Equipment List</h2></Col>
        <Col className="text-end">
          <Button variant="primary" onClick={handleAdd}>Add Equipment</Button>
          <Button variant="primary" className="ms-2" onClick={() => navigate('/equipments/activity/create')}>Add Equipment Activity</Button>
        </Col>
      </Row>
      {error && <Alert variant="danger">{error}</Alert>}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={3} className="text-center"><Spinner animation="border" size="sm" /></td></tr>
          ) : equipments.length === 0 ? (
            <tr><td colSpan={3} className="text-center">No equipment found</td></tr>
          ) : equipments.map((e, idx) => (
            <React.Fragment key={e.id}>
              <tr
                style={{ cursor: 'pointer' }}
                onClick={() => handleToggleEquipment(e)}
                className={expandedEquipment === e.id ? 'table-active' : ''}
              >
                <td>
                  {expandedEquipment === e.id ?
                    <i className="fas fa-chevron-down me-2"></i> :
                    <i className="fas fa-chevron-right me-2"></i>
                  }
                  {idx + 1}
                </td>
                <td>{e.name}</td>
                <td onClick={(event) => event.stopPropagation()}>
                  <Button variant="primary" size="sm" className="me-2" onClick={() => handleEdit(e)}>
                    <i className="fas fa-edit"></i>
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(e.id)} disabled={deleteId === e.id}>
                    {deleteId === e.id ? <Spinner animation="border" size="sm" /> : <i className="fas fa-trash"></i>}
                  </Button>
                </td>
              </tr>
              {expandedEquipment === e.id && (
                <tr>
                  <td colSpan={3} className="p-0">
                    <div className="p-3 bg-light border-top">
                      <h6 className="mb-3">Activities for {e.name}</h6>
                      {loadingActivities[e.id] ? (
                        <div className="text-center p-3">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Loading activities...</span>
                        </div>
                      ) : activitiesError[e.id] ? (
                        <Alert variant="danger" className="mb-0">{activitiesError[e.id]}</Alert>
                      ) : !activities[e.id] || activities[e.id].length === 0 ? (
                        <Alert variant="info" className="mb-0">No activities found for this equipment.</Alert>
                      ) : (
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          {activities[e.id].map((activity) => (
                            <div key={activity.id} className="border-bottom py-2 px-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <span className="fw-medium">{activity.activity_name}</span>
                                    {activity.control && (
                                      <span className="badge bg-secondary badge-sm">{activity.control}</span>
                                    )}
                                  </div>
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    {activity.options && (
                                      <small className="text-muted">
                                        <i className="fas fa-list-ul me-1"></i>
                                        {activity.options.length > 50 ? `${activity.options.substring(0, 50)}...` : activity.options}
                                      </small>
                                    )}
                                    {activity.created_at && (
                                      <small className="text-muted">
                                        <i className="fas fa-clock me-1"></i>
                                        {new Date(activity.created_at).toLocaleDateString()}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 text-end">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => navigate(`/equipments/activity/create`)}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Add New Activity
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add Equipment' : 'Edit Equipment'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleModalSave}>
          <Modal.Body>
            <Form.Group controlId="equipmentName">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} required autoFocus />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>


    </Container>
  );
};

export default EquipmentList; 